/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { BackgroundContainer, CosmosAsset, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { css, cx } from "@panda/css";
import { TabbedBlogCardsItemFragment, TabbedBlogCardsSectionFragment } from "@cosmos/hygraph-schema";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TabbedButtonList } from "@components/elements/molecules/tabbed-button-list";
import { Box, VStack } from "@panda/jsx";
import { SectionHeading } from "@components/elements/molecules/section-heading";
import { TabbedBlogCardsMolecule } from "./TabbedBlogCards.molecule";

const analyticsEventValue = {
	component: "TabbedBlogCards",
	section: "section",
};
const bgCircularGradient = `linear-gradient(270deg, rgba(16, 16, 20, 0) 70%, #101014 100%), linear-gradient(180deg, #101014 0%, rgba(16, 16, 20, 0) 75%), linear-gradient(90deg, rgba(16, 16, 20, 0) 50%, #101014 100%), linear-gradient(180deg, rgba(16, 16, 20, 0) 50%, #101014 100%)`;

const bgOverlayStyles = css({
	position: "absolute",
	inset: "0",
	display: "flex",
	"& img, & video": {
		height: "100%",
		width: "100%",
		objectFit: "cover",
	},
	"&::after": {
		content: "''",
		display: "block",
		background: bgCircularGradient,
		position: "absolute",
		inset: 0,
		zIndex: 1,
	},
	zIndex: 0,
	"& .cosmos-attribution": {
		zIndex: 3,
	},
});

const buttonListStyle = css({
	gap: "eds.dimension.12",
	"& button": { paddingInline: "eds.dimension.16" },
	alignSelf: "start",
});

const sectionHeadingStyles = css({
	"& .title-desc-stack": {
		rowGap: "eds.dimension.16",
	},
});

export const TabbedBlogCardsSection = (props: TabbedBlogCardsSectionFragment): React.JSX.Element | null => {
	const tabs = useMemo(() => props?.tabs || [], [props?.tabs]);
	const active = tabs[0] || {};
	const [activeTab, setActiveTab] = useState<TabbedBlogCardsItemFragment>(active);

	useEffect(() => {
		if (tabs.length > 0 && !activeTab?.id) setActiveTab(tabs[0]);
	}, [tabs, activeTab]);

	const tabsRef = useRef(tabs);
	useEffect(() => {
		tabsRef.current = tabs;
	}, [tabs]);

	const handleButtonClick = useCallback((index: number) => {
		const tab = Number.isInteger(index) ? tabsRef.current.at(index) : undefined;
		if (tab) setActiveTab(tab);
	}, []);

	const validHeading = useMemo(
		() => Boolean(activeTab?.title || activeTab?.description),
		[activeTab?.title, activeTab?.description],
	);
	const validContent = useMemo(
		() => Boolean(tabs.length && activeTab?.cards?.length),
		[tabs?.length, activeTab?.cards?.length],
	);

	if (!validHeading && !validContent) return null;

	const enableTabs = tabs.length > 1;
	const featuredCardsLength = activeTab?.featuredCards?.length ?? 0;
	const cardsLength = activeTab?.cards?.length ?? 0;

	return (
		<BackgroundContainer>
			<ResponsiveContainer>
				<SectionContainer>
					{activeTab?.backgroundAsset ? (
						<Box className={cx("tabbed-blog-cards-bg-container", bgOverlayStyles)}>
							<CosmosAsset asset={activeTab?.backgroundAsset}></CosmosAsset>
						</Box>
					) : null}
					<VStack
						gap="eds.dimension.32"
						alignItems="flex-start"
						className="tabbed-blog-cards-section"
						position="relative"
					>
						{enableTabs ? (
							<TabbedButtonList
								onTabChange={handleButtonClick}
								tabs={tabs}
								analyticsEventValue={analyticsEventValue}
								className={buttonListStyle}
								buttonSize="lg"
							/>
						) : null}
						<VStack gap="eds.dimension.48" alignItems="flex-start" w="full">
							{validHeading ? (
								<SectionHeading
									title={activeTab?.title}
									desc={activeTab?.description}
									className={sectionHeadingStyles}
									buttons={activeTab?.buttons}
									buttonAlignLeft={false}
								/>
							) : null}

							{featuredCardsLength ? (
								<TabbedBlogCardsMolecule
									cards={activeTab?.featuredCards}
									isFirstCardRow={featuredCardsLength === 1 || featuredCardsLength > 4}
									cardGridFitToWidth={true}
								/>
							) : null}
							{cardsLength ? (
								<TabbedBlogCardsMolecule
									cards={activeTab?.cards}
									isFirstCardRow={!!activeTab?.cardGridFitToWidth && cardsLength === 1}
									cardGridFitToWidth={!!activeTab?.cardGridFitToWidth}
								/>
							) : null}
						</VStack>
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
